import { get } from "lodash";
import { createSelector } from "reselect";

import { OPERATIONS } from "../../../constants/operations.js";

// -- QUERIES -- //

const authenticationSubQuery = state => get(state, "authentication.sub", null);
const timezonesMapQuery = state => get(state, "timezones.dataMap", null);
const accountQuery = state => get(state, "account.data", null);
const timezoneIdQuery = state => get(state, "account.data.timezone", null);
const sshKeysQuery = state => get(state, "account.data.sshKeys", null);
const featuresQuery = state => get(state, "account.data.features", null);

// -- SELECTORS -- //

const selectTimezoneName = createSelector(
  [timezonesMapQuery, timezoneIdQuery],
  (timezonesMap, timezoneId) => {
    if (!timezonesMap || !timezoneId) return "";
    return timezonesMap[timezoneId].value;
  }
);

const selectUserSub = createSelector(
  [authenticationSubQuery],
  authenticationSub => authenticationSub
);

// for use in any sshKey dropdowns
const selectSSHKeyOptionData = createSelector([sshKeysQuery], sshKeys => {
  let options = [];

  // user schedules
  if (sshKeys) {
    options = Object.keys(sshKeys).map(id => {
      const item = sshKeys[id];

      return {
        key: item["@id"],
        text: item.name,
        value: item["@id"]
      };
    });
  }

  return options;
});

const selectAccount = createSelector(
  [accountQuery, timezonesMapQuery],
  (account, timezonesMap) => {
    if (!account || !timezonesMap) return null;

    const timezone = timezonesMap[account.timezone];

    return {
      ...account,
      timezone
    };
  }
);

const selectAccountFieldUpdateAvailability = (account, fieldName) => {
  if (!account || !account.operations) return false;

  const operation = account.operations.find(
    element => element.name === OPERATIONS.UPDATE
  );

  const result = operation.fields.find(element => element.name === fieldName);
  return result !== undefined;
};

const selectDetailsUpdateAvailability = createSelector(
  [accountQuery],
  account => {
    const firstName = selectAccountFieldUpdateAvailability(
      account,
      "firstName"
    );
    const lastName = selectAccountFieldUpdateAvailability(account, "lastName");

    return firstName && lastName;
  }
);

const selectUsernameUpdateAvailability = createSelector(
  [accountQuery],
  account => {
    return selectAccountFieldUpdateAvailability(account, "username");
  }
);

const selectEmailUpdateAvailability = createSelector(
  [accountQuery],
  account => {
    return selectAccountFieldUpdateAvailability(account, "email");
  }
);

const selectPasswordUpdateAvailability = createSelector(
  [accountQuery],
  account => {
    return selectAccountFieldUpdateAvailability(account, "password");
  }
);

// TODO
// - Expand feature flags with versions to allow for better api and version comparison.
// - For example:
//
// export const FLAGS = {
//   DASHBOARD: {
//      name: "feature_dashboard_",
//      versions: ["v1", "v2.6", "v3"]
//   },
//   PROJECTS: {
//      name: "feature_projects_",
//      versions: ["v1", "v2.6", "v3"]
//   }
// }
//

export const selectFeatureFlag = createSelector(
  [featuresQuery],
  features => flagId => {
    if (!features || !flagId) return false;

    const flagFound = features.find(item => {
      return item === flagId;
    });

    return flagFound !== undefined;
  }
);

// using the following export pattern so we can mock more easily using jest

const accountSelectors = {
  selectAccount,
  selectTimezoneName,
  selectUserSub,
  selectSSHKeyOptionData,
  selectUsernameUpdateAvailability,
  selectEmailUpdateAvailability,
  selectPasswordUpdateAvailability,
  selectDetailsUpdateAvailability,
  selectFeatureFlag
};

export default accountSelectors;
